import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonCol, IonGrid, IonItemDivider, IonLabel, IonRow, useIonViewDidEnter } from '@ionic/react'
import { IRootState } from '../../../store'

import '../page/Times.page.css'
import TimesRow from '../timesRow/TimesRow.component'
import { TimesConstants } from '../store/times.constants'
import { useTranslation } from 'react-i18next'
import {
  reRunTimesValidation,
  setOnsetDateTimePNNV,
  setReceivedDateTimePNNV,
  setEnRouteDateTimePNNV,
  setOnSceneDateTimePNNV,
  setAtPatientDateTimePNNV,
  setLeaveReferringDateTimePNNV,
  setArriveReceivingDateTimePNNV,
  setTransferCareEMSDateTimePNNV,
  setTransferCareDestinationDateTimePNNV,
} from '../store/times.actions'
import { getAllPNNVOptionsSelector } from '../../options/selectors'
import { IPNNVOption } from '../../../common/common.types'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../vitals/store/vitals.actions'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { showAtLanding, showDescent } from '../utils/timesGrid.util'

const TimesGrid: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const times = useSelector((state: IRootState) => state.times)
  const configurationsState: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { AIR } = configurationsState.serviceConfig
  const { unit } = useSelector(getTripInformationSelector)
  const {
    onsetTime,
    onsetDateTimePNNV,
    receivedDateTimePNNV,
    receivedTime,
    dispatchNotifiedTime,
    standbyTime,
    dispatchedTime,
    acknowledgedTime,
    enRouteTime,
    onsetDate,
    receivedDate,
    dispatchNotifiedDate,
    standbyDate,
    dispatchedDate,
    acknowledgedDate,
    enRouteDate,
    stagingTime,
    onSceneTime,
    atPatientTime,
    transferCareEMSTime,
    leaveReferringTime,
    arriveReceivingTime,
    transferCareDestinationTime,
    availableTime,
    callCompletedTime,
    inQuartersTime,
    cancelledTime,
    firstResponderTime,
    stagingDate,
    onSceneDate,
    atPatientDate,
    transferCareEMSDate,
    leaveReferringDate,
    arriveReceivingDate,
    transferCareDestinationDate,
    availableDate,
    callCompletedDate,
    inQuartersDate,
    cancelledDate,
    firstResponderDate,
    enRouteDateTimePNNV,
    onSceneDateTimePNNV,
    atPatientDateTimePNNV,
    transferCareEMSDateTimePNNV,
    leaveReferringDateTimePNNV,
    arriveReceivingDateTimePNNV,
    transferCareDestinationDateTimePNNV,
    leavePatientDate,
    leavePatientTime,
    descentDate,
    descentTime,
    atLandingDate,
    atLandingTime,
    custom1DateTimeLabel,
    custom1Date,
    custom1Time,
    custom2DateTimeLabel,
    custom2Date,
    custom2Time,
    custom3DateTimeLabel,
    custom3Date,
    custom3Time,
    custom4DateTimeLabel,
    custom4Date,
    custom4Time,
    __hideOnset,
    __hideReceived,
    __hideDispatchNotified,
    __hideStandby,
    __hideAcknowledged,
    __hideEnRoute,
    __hideStaging,
    __hideOnScene,
    __hideAtPatient,
    __hideTransferCareEMS,
    __hideLeaveReferring,
    __hideArriveReceiving,
    __hideTransferCareDestination,
    __hideCallCompleted,
    __hideCancelled,
    __hideFirstResponder,
    __hideInQuarters,
    __hideLeavePatient,
    __hideDescentForDisposition,
    __disableDescentForDisposition,
    __hideAtLandingForDisposition,
    __disableAtLandingForDisposition,
    __disableAcknowledged,
    __disableArriveReceiving,
    __disableCallCompleted,
    __disableCancelled,
    __disableDispatchNotified,
    __disableEnRoute,
    __disableLeaveReferring,
    __disableOnScene,
    __disableReceived,
    __disableStandby,
    __disableStaging,
    __hideCustom1DateTime,
    __hideCustom2DateTime,
    __hideCustom3DateTime,
    __hideCustom4DateTime,
    __disableCustom1DateTime,
    __disableCustom2DateTime,
    __disableCustom3DateTime,
    __disableCustom4DateTime,
    __disableAtPatientForDisposition,
    __disableFirstResponderForDisposition,
    __disableTransferCareDestinationForDisposition,
    __disableTransferCareEMSForDisposition,
    __disableArriveReceivingForDisposition,
    __hideCancelledForDisposition,
    __hideTransferCareDestinationForDisposition,
    __hideTransferCareEMSForDisposition,
    __hideArriveReceivingForDisposition,
    __hideFirstResponderForDisposition,
    __hideAtPatientForDisposition,
    __disableLeavePatientForDisposition,
    __hideLeavePatientForDisposition,
  } = times

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const onsetTimePNNVOptions = allPNNVOptions?.['eSituation.01']
  const onReceivedPNNVOptions = allPNNVOptions?.['eTimes.01']
  const enRouteDateTimePNNVOptions = allPNNVOptions?.['eTimes.05']
  const onSceneDateTimePNNVOptions = allPNNVOptions?.['eTimes.06']
  const atPatientDateTimePNNVOptions = allPNNVOptions?.['eTimes.07']
  const transferCareEMSDateTimePNNVOptions = allPNNVOptions?.['eTimes.08']
  const leaveReferringDateTimePNNVOptions = allPNNVOptions?.['eTimes.09']
  const arriveReceivingDateTimePNNVOptions = allPNNVOptions?.['eTimes.11']
  const transferCareDestinationDateTimePNNVOptions = allPNNVOptions?.['eTimes.12']

  const isDescentVisible = showDescent(unit, AIR)
  const isAtLandingVisible = showAtLanding(unit)

  useIonViewDidEnter(() => {
    dispatch(reRunTimesValidation())
  })

  const isApproximateSelected = (pnnValue: IPNNVOption | null) => {
    return pnnValue?.id === 8801029
  }

  const actionConstants = {
    onSet: {
      time: TimesConstants.SET_ONSET_TIME,
      date: TimesConstants.SET_ONSET_DATE,
    },
    receivedTime: {
      time: TimesConstants.SET_RECEIVED_TIME,
      date: TimesConstants.SET_RECEIVED_DATE,
    },
    dispatchNotified: {
      time: TimesConstants.SET_DISPATCH_NOTIFIED_TIME,
      date: TimesConstants.SET_DISPATCH_NOTIFIED_DATE,
    },
    standBy: {
      time: TimesConstants.SET_STANDBY_TIME,
      date: TimesConstants.SET_STANDBY_DATE,
    },
    dispatched: {
      time: TimesConstants.SET_DISPATCHED_TIME,
      date: TimesConstants.SET_DISPATCHED_DATE,
    },
    acknowledged: {
      time: TimesConstants.SET_ACKNOWLEDGED_TIME,
      date: TimesConstants.SET_ACKNOWLEDGED_DATE,
    },
    enRoute: {
      time: TimesConstants.SET_EN_ROUTE_TIME,
      date: TimesConstants.SET_EN_ROUTE_DATE,
    },
    staging: {
      time: TimesConstants.SET_STAGING_TIME,
      date: TimesConstants.SET_STAGING_DATE,
    },
    onScene: {
      time: TimesConstants.SET_ON_SCENE_TIME,
      date: TimesConstants.SET_ON_SCENE_DATE,
    },
    firstResponder: {
      time: TimesConstants.SET_FIRST_RESPONDER_TIME,
      date: TimesConstants.SET_FIRST_RESPONDER_DATE,
    },
    atPatient: {
      time: TimesConstants.SET_AT_PATIENT_TIME,
      date: TimesConstants.SET_AT_PATIENT_DATE,
    },
    transferCareEMS: {
      time: TimesConstants.SET_TRANSFER_CARE_EMS_TIME,
      date: TimesConstants.SET_TRANSFER_CARE_EMS_DATE,
    },
    leaveReferring: {
      time: TimesConstants.SET_LEAVE_REFERRING_TIME,
      date: TimesConstants.SET_LEAVE_REFERRING_DATE,
    },
    arriveReceiving: {
      time: TimesConstants.SET_ARRIVE_RECEIVING_TIME,
      date: TimesConstants.SET_ARRIVE_RECEIVING_DATE,
    },
    transferCareDestination: {
      time: TimesConstants.SET_TRANSFER_CARE_DESTINATION_TIME,
      date: TimesConstants.SET_TRANSFER_CARE_DESTINATION_DATE,
    },
    available: {
      time: TimesConstants.SET_AVAILABLE_TIME,
      date: TimesConstants.SET_AVAILABLE_DATE,
    },
    callCompleted: {
      time: TimesConstants.SET_CALL_COMPLETED_TIME,
      date: TimesConstants.SET_CALL_COMPLETED_DATE,
    },
    inQuarters: {
      time: TimesConstants.SET_IN_QUARTERS_TIME,
      date: TimesConstants.SET_IN_QUARTERS_DATE,
    },
    cancelled: {
      time: TimesConstants.SET_CANCELLED_TIME,
      date: TimesConstants.SET_CANCELLED_DATE,
    },
    leavePatient: {
      time: TimesConstants.SET_LEAVE_PATIENT_TIME,
      date: TimesConstants.SET_LEAVE_PATIENT_DATE,
    },
    descent: {
      time: TimesConstants.SET_DESCENT_TIME,
      date: TimesConstants.SET_DESCENT_DATE,
    },
    atLanding: {
      time: TimesConstants.SET_AT_LANDING_TIME,
      date: TimesConstants.SET_AT_LANDING_DATE,
    },
    custom1DateTime: {
      time: TimesConstants.SET_CUSTOM_1_TIME,
      date: TimesConstants.SET_CUSTOM_1_DATE,
    },
    custom2DateTime: {
      time: TimesConstants.SET_CUSTOM_2_TIME,
      date: TimesConstants.SET_CUSTOM_2_DATE,
    },
    custom3DateTime: {
      time: TimesConstants.SET_CUSTOM_3_TIME,
      date: TimesConstants.SET_CUSTOM_3_DATE,
    },
    custom4DateTime: {
      time: TimesConstants.SET_CUSTOM_4_TIME,
      date: TimesConstants.SET_CUSTOM_4_DATE,
    },
  }

  const clearDateTime = (dateAction: string, timeAction: string) => {
    dispatch({ type: timeAction, time: '' })
    dispatch({ type: dateAction, date: '' })
  }

  const handleOnSetDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if ((!onsetDate || !onsetTime) && isApproximateSelected(pnnvOption)) {
      const alertState: IAlertState = {
        header: '',
        message: 'To select approximate, Onset Date and Time is first required',
        buttons: [{ text: t('pnnv.ok') }],
      }
      dispatch(showAlert(alertState))
    } else {
      dispatch(setOnsetDateTimePNNV(pnnvOption))
    }
    if (!isApproximateSelected(pnnvOption) && pnnvOption)
      clearDateTime(actionConstants.onSet.date, actionConstants.onSet.time)
  }

  const handleReceivedDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.receivedTime.date, actionConstants.receivedTime.time)
    dispatch(setReceivedDateTimePNNV(pnnvOption))
  }

  const handleEnRouteDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.enRoute.date, actionConstants.enRoute.time)
    dispatch(setEnRouteDateTimePNNV(pnnvOption))
  }

  const handleOnSceneDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.onScene.date, actionConstants.onScene.time)
    dispatch(setOnSceneDateTimePNNV(pnnvOption))
  }

  const handleAtPatientDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.atPatient.date, actionConstants.atPatient.time)
    dispatch(setAtPatientDateTimePNNV(pnnvOption))
  }

  const handleTransferCareEmsDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.transferCareEMS.date, actionConstants.transferCareEMS.time)
    dispatch(setTransferCareEMSDateTimePNNV(pnnvOption))
  }

  const handleLeaveReferringDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.leaveReferring.date, actionConstants.leaveReferring.time)
    dispatch(setLeaveReferringDateTimePNNV(pnnvOption))
  }

  const handleArriveReceivingDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption) clearDateTime(actionConstants.arriveReceiving.date, actionConstants.arriveReceiving.time)
    dispatch(setArriveReceivingDateTimePNNV(pnnvOption))
  }

  const handleTransferCarDestinationDateTimePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    if (pnnvOption)
      clearDateTime(actionConstants.transferCareDestination.date, actionConstants.transferCareDestination.time)
    dispatch(setTransferCareDestinationDateTimePNNV(pnnvOption))
  }

  return (
    <IonGrid class='no-padding times-grid'>
      <IonRow className='times-header ion-padding-top' id='times-header'>
        <IonCol className='ion-padding-start ion-hide-md-down' sizeMd='3'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('times.dispatchTimes')}
          </IonLabel>
        </IonCol>
        <IonCol className='ion-hide-md-down' sizeMd='2'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('times.tapToAddTime')}
          </IonLabel>
        </IonCol>
        <IonCol className='times-current-time-header' pushXs='2' pushMd='0' sizeMd='2'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('times.addCurrentTime')}
          </IonLabel>
        </IonCol>
        <IonCol className='ion-hide-md-down tap-to-add-date-header'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('times.tapToAddDate')}
          </IonLabel>
        </IonCol>
      </IonRow>

      {!__hideOnset && (
        <>
          <TimesRow
            elementId='times-onset'
            header={t('times.onset')}
            isStopWatchDisabled={onsetDateTimePNNV && !isApproximateSelected(onsetDateTimePNNV)}
            time={onsetTime}
            date={onsetDate}
            timeAction={actionConstants.onSet.time}
            dateAction={actionConstants.onSet.date}
            currentDateTimeAction={TimesConstants.ADD_ONSET_CURRENT_DATETIME}
            pnnvAction={handleOnSetDateTimePNNVOnChange}
            pnnvListHeader={t('times.onset')}
            pnnvOptions={onsetTimePNNVOptions}
            pnnvValue={onsetDateTimePNNV}
            useBothNormalAndPertinent={isApproximateSelected(onsetDateTimePNNV)}
            overridePicklistWithNumberPad={!isApproximateSelected(onsetDateTimePNNV)}
          />
          <hr />
        </>
      )}
      {!__hideReceived && (
        <>
          <TimesRow
            elementId='times-received'
            header={t('times.received')}
            time={receivedTime}
            date={receivedDate}
            disabled={__disableReceived}
            isStopWatchDisabled={__disableReceived}
            timeAction={actionConstants.receivedTime.time}
            dateAction={actionConstants.receivedTime.date}
            currentDateTimeAction={TimesConstants.ADD_RECEIVED_CURRENT_DATETIME}
            pnnvAction={handleReceivedDateTimePNNVOnChange}
            pnnvListHeader={t('times.received')}
            pnnvOptions={onReceivedPNNVOptions}
            pnnvValue={receivedDateTimePNNV}
          />
          <hr />
        </>
      )}

      {!__hideDispatchNotified && (
        <>
          <TimesRow
            elementId='times-dispatchNotified'
            header={t('times.dispatchNotified')}
            time={dispatchNotifiedTime}
            date={dispatchNotifiedDate}
            disabled={__disableDispatchNotified}
            isStopWatchDisabled={__disableDispatchNotified}
            timeAction={actionConstants.dispatchNotified.time}
            dateAction={actionConstants.dispatchNotified.date}
            currentDateTimeAction={TimesConstants.ADD_DISPATCH_NOTIFIED_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideStandby && (
        <>
          <TimesRow
            elementId='times-standby'
            header={t('times.standby')}
            time={standbyTime}
            date={standbyDate}
            disabled={__disableStandby}
            isStopWatchDisabled={__disableStandby}
            timeAction={actionConstants.standBy.time}
            dateAction={actionConstants.standBy.date}
            currentDateTimeAction={TimesConstants.ADD_STANDBY_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      <TimesRow
        elementId='times-dispatched'
        header={t('times.dispatched')}
        time={dispatchedTime}
        date={dispatchedDate}
        timeAction={actionConstants.dispatched.time}
        dateAction={actionConstants.dispatched.date}
        currentDateTimeAction={TimesConstants.ADD_DISPATCHED_CURRENT_DATETIME}
      />
      <hr />

      {!__hideAcknowledged && (
        <>
          <TimesRow
            elementId='times-acknowledged'
            header={t('times.acknowledged')}
            disabled={__disableAcknowledged}
            isStopWatchDisabled={__disableAcknowledged}
            time={acknowledgedTime}
            date={acknowledgedDate}
            timeAction={actionConstants.acknowledged.time}
            dateAction={actionConstants.acknowledged.date}
            currentDateTimeAction={TimesConstants.ADD_ACKNOWLEDGED_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideEnRoute && (
        <>
          <TimesRow
            elementId='times-enroute'
            header={t('times.enRoute')}
            time={enRouteTime}
            date={enRouteDate}
            disabled={__disableEnRoute}
            isStopWatchDisabled={__disableEnRoute}
            timeAction={actionConstants.enRoute.time}
            dateAction={actionConstants.enRoute.date}
            currentDateTimeAction={TimesConstants.ADD_EN_ROUTE_CURRENT_DATETIME}
            pnnvAction={handleEnRouteDateTimePNNVOnChange}
            pnnvListHeader={t('times.enRoute')}
            pnnvOptions={enRouteDateTimePNNVOptions}
            pnnvValue={enRouteDateTimePNNV}
          />
          <hr />
        </>
      )}

      {!__hideStaging && (
        <>
          <TimesRow
            elementId='times-staging'
            header={t('times.staging')}
            time={stagingTime}
            date={stagingDate}
            disabled={__disableStaging}
            isStopWatchDisabled={__disableStaging}
            timeAction={actionConstants.staging.time}
            dateAction={actionConstants.staging.date}
            currentDateTimeAction={TimesConstants.ADD_STAGING_CURRENT_DATETIME}
          />
        </>
      )}

      <IonItemDivider color='light' />

      {!__hideOnScene && (
        <>
          <TimesRow
            elementId='times-onScene'
            header={t('times.onScene')}
            time={onSceneTime}
            date={onSceneDate}
            disabled={__disableOnScene}
            isStopWatchDisabled={__disableOnScene}
            timeAction={actionConstants.onScene.time}
            dateAction={actionConstants.onScene.date}
            currentDateTimeAction={TimesConstants.ADD_ON_SCENE_CURRENT_DATETIME}
            pnnvAction={handleOnSceneDateTimePNNVOnChange}
            pnnvListHeader={t('times.onScene')}
            pnnvOptions={onSceneDateTimePNNVOptions}
            pnnvValue={onSceneDateTimePNNV}
          />
          <hr />
        </>
      )}
      {!__hideFirstResponder && !__hideFirstResponderForDisposition && (
        <>
          <TimesRow
            elementId='times-firstResponder'
            header={t('times.firstResponder')}
            disabled={__disableFirstResponderForDisposition}
            isStopWatchDisabled={__disableFirstResponderForDisposition}
            time={firstResponderTime}
            date={firstResponderDate}
            timeAction={actionConstants.firstResponder.time}
            dateAction={actionConstants.firstResponder.date}
            currentDateTimeAction={TimesConstants.ADD_FIRST_RESPONDER_DATETIME}
          />
          <hr />
        </>
      )}
      {!__hideAtPatient && !__hideAtPatientForDisposition && (
        <>
          <TimesRow
            elementId='times-atPatient'
            header={t('times.atPatient')}
            time={atPatientTime}
            date={atPatientDate}
            disabled={__disableAtPatientForDisposition}
            isStopWatchDisabled={__disableAtPatientForDisposition}
            timeAction={actionConstants.atPatient.time}
            dateAction={actionConstants.atPatient.date}
            currentDateTimeAction={TimesConstants.ADD_AT_PATIENT_CURRENT_DATETIME}
            pnnvAction={handleAtPatientDateTimePNNVOnChange}
            pnnvListHeader={t('times.atPatient')}
            pnnvOptions={atPatientDateTimePNNVOptions}
            pnnvValue={atPatientDateTimePNNV}
          />
          <hr />
        </>
      )}

      {!__hideLeavePatient && !__hideLeavePatientForDisposition && (
        <>
          <TimesRow
            elementId='times-leavePatient'
            header={t('times.leavePatient')}
            time={leavePatientTime}
            date={leavePatientDate}
            disabled={__disableLeavePatientForDisposition}
            isStopWatchDisabled={__disableLeavePatientForDisposition}
            timeAction={actionConstants.leavePatient.time}
            dateAction={actionConstants.leavePatient.date}
            currentDateTimeAction={TimesConstants.ADD_LEAVE_PATIENT_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideTransferCareEMS && !__hideTransferCareEMSForDisposition && (
        <>
          <TimesRow
            elementId='times-transferCareEMS'
            header={t('times.transferCareEMS')}
            time={transferCareEMSTime}
            date={transferCareEMSDate}
            disabled={__disableTransferCareEMSForDisposition}
            isStopWatchDisabled={__disableTransferCareEMSForDisposition}
            timeAction={actionConstants.transferCareEMS.time}
            dateAction={actionConstants.transferCareEMS.date}
            currentDateTimeAction={TimesConstants.ADD_TRANSFER_CARE_EMS_CURRENT_DATETIME}
            pnnvAction={handleTransferCareEmsDateTimePNNVOnChange}
            pnnvListHeader={t('times.transferCareEMS')}
            pnnvOptions={transferCareEMSDateTimePNNVOptions}
            pnnvValue={transferCareEMSDateTimePNNV}
          />
          <hr />
        </>
      )}

      {!__hideLeaveReferring && (
        <>
          <TimesRow
            elementId='times-leaveReferring'
            header={t('times.leaveReferring')}
            time={leaveReferringTime}
            date={leaveReferringDate}
            disabled={__disableLeaveReferring}
            isStopWatchDisabled={__disableLeaveReferring}
            timeAction={actionConstants.leaveReferring.time}
            dateAction={actionConstants.leaveReferring.date}
            currentDateTimeAction={TimesConstants.ADD_LEAVE_REFERRING_CURRENT_DATETIME}
            pnnvAction={handleLeaveReferringDateTimePNNVOnChange}
            pnnvListHeader={t('times.leaveReferring')}
            pnnvOptions={leaveReferringDateTimePNNVOptions}
            pnnvValue={leaveReferringDateTimePNNV}
          />
        </>
      )}

      {isDescentVisible && !__hideDescentForDisposition && (
        <>
          <hr />
          <TimesRow
            elementId='times-descent'
            header={t('times.descent')}
            time={descentTime}
            date={descentDate}
            disabled={__disableDescentForDisposition}
            isStopWatchDisabled={__disableDescentForDisposition}
            timeAction={actionConstants.descent.time}
            dateAction={actionConstants.descent.date}
            currentDateTimeAction={TimesConstants.ADD_DESCENT_CURRENT_DATETIME}
          />
        </>
      )}

      {isAtLandingVisible && !__hideAtLandingForDisposition && (
        <>
          <hr />
          <TimesRow
            elementId='times-atLanding'
            header={t('times.atLanding')}
            time={atLandingTime}
            date={atLandingDate}
            disabled={__disableAtLandingForDisposition}
            isStopWatchDisabled={__disableAtLandingForDisposition}
            timeAction={actionConstants.atLanding.time}
            dateAction={actionConstants.atLanding.date}
            currentDateTimeAction={TimesConstants.ADD_AT_LANDING_CURRENT_DATETIME}
          />
        </>
      )}

      <IonItemDivider color='light' />

      {!__hideArriveReceiving && !__hideArriveReceivingForDisposition && (
        <>
          <TimesRow
            elementId='times-arriveReceiving'
            header={t('times.arriveReceiving')}
            time={arriveReceivingTime}
            date={arriveReceivingDate}
            disabled={__disableArriveReceiving || __disableArriveReceivingForDisposition}
            isStopWatchDisabled={__disableArriveReceiving || __disableArriveReceivingForDisposition}
            timeAction={actionConstants.arriveReceiving.time}
            dateAction={actionConstants.arriveReceiving.date}
            currentDateTimeAction={TimesConstants.ADD_ARRIVE_RECEIVING_CURRENT_DATETIME}
            pnnvAction={handleArriveReceivingDateTimePNNVOnChange}
            pnnvListHeader={t('times.arriveReceiving')}
            pnnvOptions={arriveReceivingDateTimePNNVOptions}
            pnnvValue={arriveReceivingDateTimePNNV}
          />
          <hr />
        </>
      )}

      {!__hideTransferCareDestination && !__hideTransferCareDestinationForDisposition && (
        <>
          <TimesRow
            elementId='times-transferCareDestination'
            header={t('times.transferCareDestination')}
            time={transferCareDestinationTime}
            date={transferCareDestinationDate}
            disabled={__disableTransferCareDestinationForDisposition}
            isStopWatchDisabled={__disableTransferCareDestinationForDisposition}
            timeAction={actionConstants.transferCareDestination.time}
            dateAction={actionConstants.transferCareDestination.date}
            currentDateTimeAction={TimesConstants.ADD_TRANSFER_CARE_DESTINATION_CURRENT_DATETIME}
            pnnvAction={handleTransferCarDestinationDateTimePNNVOnChange}
            pnnvListHeader={t('times.transferCareDestination')}
            pnnvOptions={transferCareDestinationDateTimePNNVOptions}
            pnnvValue={transferCareDestinationDateTimePNNV}
          />
          <hr />
        </>
      )}

      <TimesRow
        elementId='times-available'
        header={t('times.available')}
        time={availableTime}
        date={availableDate}
        timeAction={actionConstants.available.time}
        dateAction={actionConstants.available.date}
        currentDateTimeAction={TimesConstants.ADD_AVAILABLE_CURRENT_DATETIME}
      />
      <hr />

      {!__hideCallCompleted && (
        <>
          <TimesRow
            elementId='times-callCompleted'
            header={t('times.callCompleted')}
            time={callCompletedTime}
            date={callCompletedDate}
            disabled={__disableCallCompleted}
            isStopWatchDisabled={__disableCallCompleted}
            timeAction={actionConstants.callCompleted.time}
            dateAction={actionConstants.callCompleted.date}
            currentDateTimeAction={TimesConstants.ADD_CALL_COMPLETED_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideInQuarters && (
        <>
          <TimesRow
            elementId='times-inQuarters'
            header={t('times.inQuarters')}
            time={inQuartersTime}
            date={inQuartersDate}
            timeAction={actionConstants.inQuarters.time}
            dateAction={actionConstants.inQuarters.date}
            currentDateTimeAction={TimesConstants.ADD_IN_QUARTERS_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideCancelled && (
        <>
          <TimesRow
            elementId='times-cancelled'
            header={t('times.cancelled')}
            time={cancelledTime}
            date={cancelledDate}
            disabled={__disableCancelled}
            isStopWatchDisabled={__disableCancelled}
            timeAction={actionConstants.cancelled.time}
            dateAction={actionConstants.cancelled.date}
            currentDateTimeAction={TimesConstants.ADD_CANCELLED_CURRENT_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideCustom1DateTime && (
        <>
          <TimesRow
            elementId='times-custom1DateTime'
            header={custom1DateTimeLabel}
            time={custom1Time}
            date={custom1Date}
            disabled={__disableCustom1DateTime}
            isStopWatchDisabled={__disableCustom1DateTime}
            timeAction={actionConstants.custom1DateTime.time}
            dateAction={actionConstants.custom1DateTime.date}
            currentDateTimeAction={TimesConstants.SET_CUSTOM_1_DATETIME}
          />
          <hr />
        </>
      )}
      {!__hideCustom2DateTime && (
        <>
          <TimesRow
            elementId='times-custom2DateTime'
            header={custom2DateTimeLabel}
            time={custom2Time}
            date={custom2Date}
            disabled={__disableCustom2DateTime}
            isStopWatchDisabled={__disableCustom2DateTime}
            timeAction={actionConstants.custom2DateTime.time}
            dateAction={actionConstants.custom2DateTime.date}
            currentDateTimeAction={TimesConstants.SET_CUSTOM_2_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideCustom3DateTime && (
        <>
          <TimesRow
            elementId='times-custom3DateTime'
            header={custom3DateTimeLabel}
            time={custom3Time}
            date={custom3Date}
            disabled={__disableCustom3DateTime}
            isStopWatchDisabled={__disableCustom3DateTime}
            timeAction={actionConstants.custom3DateTime.time}
            dateAction={actionConstants.custom3DateTime.date}
            currentDateTimeAction={TimesConstants.SET_CUSTOM_3_DATETIME}
          />
          <hr />
        </>
      )}

      {!__hideCustom4DateTime && (
        <>
          <TimesRow
            elementId='times-custom4DateTime'
            header={custom4DateTimeLabel}
            time={custom4Time}
            date={custom4Date}
            disabled={__disableCustom4DateTime}
            isStopWatchDisabled={__disableCustom4DateTime}
            timeAction={actionConstants.custom4DateTime.time}
            dateAction={actionConstants.custom4DateTime.date}
            currentDateTimeAction={TimesConstants.SET_CUSTOM_4_DATETIME}
          />
          <hr />
        </>
      )}
      {!__hideCancelled && !__hideCancelledForDisposition && (
        <TimesRow
          elementId='times-cancelled'
          header={t('times.cancelled')}
          time={cancelledTime}
          date={cancelledDate}
          disabled={__disableCancelled}
          isStopWatchDisabled={__disableCancelled}
          timeAction={actionConstants.cancelled.time}
          dateAction={actionConstants.cancelled.date}
          currentDateTimeAction={TimesConstants.ADD_CANCELLED_CURRENT_DATETIME}
        />
      )}

      <IonItemDivider color='light' className='ion-padding' />
    </IonGrid>
  )
}

export default TimesGrid
